import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Search, Trash2, UserCheck, Gift, GiftIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GiftAccessDialog from "./GiftAccessDialog";

export default function AdminUsersTable({ users, q, setQ, onReload }) {
  const { user: me, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [giftTarget, setGiftTarget] = useState(null);

  const toggleAdmin = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_admin: !u.is_admin });
      toast.success(`${u.email} is ${!u.is_admin ? "now admin" : "no longer admin"}`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleSuspend = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_suspended: !u.is_suspended });
      toast.success(`${u.email} ${!u.is_suspended ? "suspended" : "reactivated"}`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (u) => {
    if (!confirm(`Delete ${u.email} and all their data? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const impersonate = async (u) => {
    if (!confirm(`Sign in as ${u.email}? You'll see exactly what they see for 1 hour. Use the banner at the top of the app to return.`)) return;
    try {
      await startImpersonation(u.id);
      toast.success(`Now viewing as ${u.email}`);
      navigate("/app");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const revokeGift = async (u) => {
    if (!confirm(`Revoke gifted access for ${u.email}? They will drop back to Free immediately.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}/gift-access`);
      toast.success(`Gift access revoked for ${u.email}`);
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const formatGiftLabel = (g) => {
    if (!g) return null;
    if (g.lifetime) return "Lifetime";
    if (!g.until) return "Gift";
    try {
      return `until ${format(parseISO(g.until), "MMM d, yyyy")}`;
    } catch {
      return "Gift";
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-display text-xl font-medium">Users</h2>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name"
            className="pl-9 w-72"
            data-testid="user-search-input"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-200">
              <th className="text-left py-2 font-medium">User</th>
              <th className="text-left py-2 font-medium">Joined</th>
              <th className="text-right py-2 font-medium">Hives</th>
              <th className="text-right py-2 font-medium">Logs</th>
              <th className="text-right py-2 font-medium">Reminders</th>
              <th className="text-right py-2 font-medium">Harvests</th>
              <th className="text-right py-2 font-medium">Diag</th>
              <th className="text-center py-2 font-medium">Admin</th>
              <th className="text-center py-2 font-medium">Active</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === me?.id;
              return (
                <tr key={u.id} className="border-b border-stone-100" data-testid={`user-row-${u.id}`}>
                  <td className="py-3">
                    <div className="font-medium text-stone-900 flex items-center gap-2 flex-wrap">
                      {u.name}
                      {u.is_admin && <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">Admin</span>}
                      {u.is_suspended && <span className="text-[10px] uppercase tracking-wider bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">Suspended</span>}
                      {u.gift_access && (
                        <span
                          className="text-[10px] uppercase tracking-wider bg-violet-100 text-violet-900 px-1.5 py-0.5 rounded flex items-center gap-1"
                          title={formatGiftLabel(u.gift_access)}
                          data-testid={`gift-badge-${u.id}`}
                        >
                          <Gift className="w-3 h-3" /> {u.gift_access.lifetime ? "Lifetime" : "Gift"}
                        </span>
                      )}
                      {isSelf && <span className="text-[10px] text-stone-500">(you)</span>}
                    </div>
                    <div className="text-xs text-stone-500">{u.email}</div>
                    {u.gift_access && !u.gift_access.lifetime && (
                      <div className="text-[11px] text-violet-700 mt-0.5">{formatGiftLabel(u.gift_access)}</div>
                    )}
                  </td>
                  <td className="py-3 text-stone-600 whitespace-nowrap">{format(parseISO(u.created_at), "MMM d, yyyy")}</td>
                  <td className="py-3 text-right tabular-nums">{u.hive_count}</td>
                  <td className="py-3 text-right tabular-nums">{u.journal_count}</td>
                  <td className="py-3 text-right tabular-nums">{u.event_count}</td>
                  <td className="py-3 text-right tabular-nums">{u.harvest_count}</td>
                  <td className="py-3 text-right tabular-nums">{u.diagnosis_count}</td>
                  <td className="py-3 text-center">
                    <Switch
                      checked={u.is_admin}
                      onCheckedChange={() => toggleAdmin(u)}
                      disabled={isSelf}
                      data-testid={`admin-toggle-${u.id}`}
                    />
                  </td>
                  <td className="py-3 text-center">
                    <Switch
                      checked={!u.is_suspended}
                      onCheckedChange={() => toggleSuspend(u)}
                      disabled={isSelf}
                      data-testid={`active-toggle-${u.id}`}
                    />
                  </td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {u.gift_access ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-violet-600 hover:text-violet-800 hover:bg-violet-50"
                          onClick={() => revokeGift(u)}
                          disabled={isSelf || u.is_admin}
                          title="Revoke gift access"
                          data-testid={`revoke-gift-${u.id}`}
                        >
                          <GiftIcon className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-stone-400 hover:text-violet-700"
                          onClick={() => setGiftTarget(u)}
                          disabled={isSelf || u.is_admin}
                          title={u.is_admin ? "Admins already have full access" : "Gift access"}
                          data-testid={`gift-user-${u.id}`}
                        >
                          <Gift className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-stone-400 hover:text-amber-700"
                        onClick={() => impersonate(u)}
                        disabled={isSelf || u.is_admin || u.is_suspended}
                        title={isSelf ? "That's you" : u.is_admin ? "Can't impersonate admins" : u.is_suspended ? "Reactivate first" : "Impersonate"}
                        data-testid={`impersonate-user-${u.id}`}
                      >
                        <UserCheck className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-stone-400 hover:text-red-600"
                        onClick={() => del(u)}
                        disabled={isSelf}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr><td colSpan={10} className="py-8 text-center text-stone-500 text-sm">No users found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <GiftAccessDialog
        user={giftTarget}
        open={!!giftTarget}
        onOpenChange={(o) => !o && setGiftTarget(null)}
        onSuccess={onReload}
      />
    </div>
  );
}
